import React, { useMemo, useRef, useState } from 'react';

import { identityGlyphColor } from '../../utils/colors';
import { neighborIndex } from '../../utils/color/honeycomb';

/**
 * A field of hexagonal color cells, picked by click, tap or arrow key. It is
 * the honeycomb of the custom color picker (CustomColorPicker.js); the gray
 * strip beside it is the same component with a one-row layout.
 *
 * An SVG, not a grid of buttons: hexagons tessellate only if each row is
 * offset by half a cell, and an HTML button clipped to a hexagon loses its
 * focus ring to the clip. The cells are `role=radio` in a `radiogroup`, with
 * one tab stop that follows the selection: arrows move AND select (a swatch
 * costs nothing to try), as they do in a native radio group.
 *
 * Selected and focused cells are told apart by stroke weight and a check
 * mark, never by color alone: a cell's own color is the data.
 */

const SQRT3_HALF = Math.sqrt(3) / 2;
// A cell is drawn a little smaller than the lattice, so the gap between
// neighbours is wide enough to hold the selection and focus strokes.
const CELL_SCALE = 0.88;
const POINTS = [
	[0, -1], [SQRT3_HALF, -0.5], [SQRT3_HALF, 0.5], [0, 1], [-SQRT3_HALF, 0.5], [-SQRT3_HALF, -0.5],
];

const hexagonPoints = (cx, cy) => POINTS
	.map(([dx, dy]) => `${(cx + (dx * CELL_SCALE)).toFixed(3)},${(cy + (dy * CELL_SCALE)).toFixed(3)}`)
	.join(' ');

const NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home'];

export default function Honeycomb({ layout, label, selectedHex, onPick, className = '', style }) {
	const { cells, box } = layout;
	const cellRefs = useRef([]);
	// The remembered column of a vertical walk (utils/color/honeycomb.js).
	const goalX = useRef(null);
	const [focusedIndex, setFocusedIndex] = useState(null);

	const middle = useMemo(() => Math.max(0, cells.findIndex(c => c.ring === 0)), [cells]);
	// Two cells never share a hex (a test holds the layouts to it), but if one
	// ever did, only the first would be marked.
	const selectedIndex = useMemo(() => cells.findIndex(c => c.hex === `${selectedHex || ''}`.toLowerCase()), [cells, selectedHex]);
	// The one tab stop: the selected cell, else where the person last was,
	// else the middle.
	const tabStop = selectedIndex >= 0
		? selectedIndex
		: (focusedIndex !== null && focusedIndex < cells.length ? focusedIndex : middle);

	const handleKeyDown = (event, index) => {
		if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
			goalX.current = null;
			onPick(cells[index]);
			return;
		}
		if (!NAVIGATION_KEYS.includes(event.key)) return;
		event.preventDefault();
		const next = neighborIndex(cells, index, event.key, goalX.current);
		goalX.current = next.goalX;
		if (next.index === index) return;
		setFocusedIndex(next.index);
		cellRefs.current[next.index]?.focus();
		onPick(cells[next.index]);
	};

	return (
		<svg
			className={`honeycomb ${className}`.trim()}
			style={style}
			viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
			role='radiogroup'
			aria-label={label}
			focusable='false'
		>
			{cells.map((cell, index) => {
				const selected = index === selectedIndex;
				return (
					<polygon
						key={`${cell.q},${cell.r}`}
						ref={node => { cellRefs.current[index] = node; }}
						className={`honeycomb__cell${selected ? ' is-selected' : ''}`}
						points={hexagonPoints(cell.x, cell.y)}
						fill={cell.hex}
						role='radio'
						aria-checked={selected}
						aria-label={cell.hex}
						tabIndex={index === tabStop ? 0 : -1}
						onClick={() => { goalX.current = null; setFocusedIndex(index); onPick(cell); }}
						onFocus={() => setFocusedIndex(index)}
						onKeyDown={event => handleKeyDown(event, index)}
					>
						<title>{cell.hex}</title>
					</polygon>
				);
			})}
			{selectedIndex >= 0 ? (
				// The check says "this one" without color: drawn over the selected
				// cell in the glyph color its own color calls for, as the palette's
				// swatches do (IconColorPicker.js).
				<path
					className='honeycomb__check'
					aria-hidden='true'
					pointerEvents='none'
					data-testid='honeycomb-check'
					d={`M ${cells[selectedIndex].x - 0.34} ${cells[selectedIndex].y + 0.02} l 0.24 0.27 l 0.46 -0.54`}
					stroke={identityGlyphColor(cells[selectedIndex].hex)}
				/>
			) : null}
		</svg>
	);
}
